import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Reusable compact calendar.
 * Props:
 *  - dates: [{id, date: 'YYYY-MM-DD', status, is_blocked, has_override}]
 *  - mode: 'navigation' | 'selection'
 *  - selectedDate: 'YYYY-MM-DD' (for selection mode)
 *  - onSelect: (dateObj) => void   -- fires on click of a configured day
 *  - initialMonth: optional Date to start on
 */
const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function pad(n) { return String(n).padStart(2, "0"); }
function fmt(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }

export default function CompactCalendar({ dates = [], mode = "navigation", selectedDate, onSelect, initialMonth, testid, hideLegend = false }) {
  const today = new Date();
  const startMonth = useMemo(() => {
    if (initialMonth) return initialMonth;
    // Snap to the month of the first configured date if today is before all dates
    if (dates.length > 0) {
      const sorted = [...dates].sort((a, b) => a.date.localeCompare(b.date));
      const todayIso = today.toISOString().slice(0, 10);
      const target = sorted.find(d => d.date >= todayIso) || sorted[sorted.length - 1];
      const [y, m] = target.date.split("-").map(Number);
      return new Date(y, m - 1, 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }, [dates.length, initialMonth]); // eslint-disable-line

  const [year, setYear] = useState(startMonth.getFullYear());
  const [month, setMonth] = useState(startMonth.getMonth());

  // Dates often load async (empty on first render), so snap once to the first
  // selectable month when they arrive — unless the caller controls the view.
  const didSnap = useRef(false);
  useEffect(() => {
    if (didSnap.current || initialMonth || selectedDate) return;
    if (dates.length > 0) {
      const sorted = [...dates].sort((a, b) => a.date.localeCompare(b.date));
      const todayIso = new Date().toISOString().slice(0, 10);
      const target = sorted.find((d) => d.date >= todayIso) || sorted[sorted.length - 1];
      const [y, m] = target.date.split("-").map(Number);
      setYear(y); setMonth(m - 1);
      didSnap.current = true;
    }
  }, [dates]); // eslint-disable-line

  // If the selected date is outside the visible month, snap to it
  useEffect(() => {
    if (!selectedDate) return;
    const [y, m] = selectedDate.split("-").map(Number);
    if (y !== year || (m - 1) !== month) {
      setYear(y); setMonth(m - 1);
    }
  }, [selectedDate]); // eslint-disable-line

  const move = (delta) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    setMonth(m); setYear(y);
  };

  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDow = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(d);

  const byDate = useMemo(() => {
    const map = {};
    dates.forEach(d => { map[d.date] = d; });
    return map;
  }, [dates]);

  const todayIso = today.toISOString().slice(0, 10);

  return (
    <div className="st-card p-4" data-testid={testid || "compact-calendar"}>
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => move(-1)} className="text-[#bbb] hover:text-white p-1" data-testid="cc-prev"><ChevronLeft size={18}/></button>
        <div className="font-semibold text-sm">{monthNames[month]} {year}</div>
        <button onClick={() => move(1)} className="text-[#bbb] hover:text-white p-1" data-testid="cc-next"><ChevronRight size={18}/></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[9px] uppercase tracking-wider text-[#7a7a7a] mb-1">
        {["M","T","W","T","F","S","S"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="aspect-square" />;
          const dstr = fmt(year, month, d);
          const ed = byDate[dstr];
          const isToday = dstr === todayIso;
          const isSelected = mode === "selection" && selectedDate === dstr;
          const configured = !!ed;
          const greyed = mode === "selection" && !configured;
          const cancelled = ed && ed.status === "cancelled";
          const blocked = ed && ed.is_blocked;

          let cls = "aspect-square rounded-md text-xs font-semibold flex items-center justify-center relative ";
          if (isSelected) cls += "bg-[#dc2626] text-white shadow-lg shadow-[#dc2626]/30 ";
          else if (cancelled) cls += "bg-[#1a0808] text-[#5a2a2a] cursor-not-allowed ";
          else if (blocked) cls += "bg-[#1a1a1a] text-[#666] ";
          else if (configured) cls += "bg-[#2a1212] text-[#fca5a5] border border-[#5b1919] hover:bg-[#3a1717] cursor-pointer ";
          else if (greyed) cls += "text-[#3a3a3a] cursor-not-allowed ";
          else cls += "text-[#7a7a7a] hover:bg-[#161616] cursor-default ";
          if (isToday && !isSelected) cls += "ring-1 ring-[#dc2626]/50 ";

          return (
            <button key={i}
                    onClick={() => configured && !cancelled && onSelect && onSelect(ed)}
                    disabled={greyed || cancelled}
                    data-testid={`cc-day-${dstr}`}
                    className={cls}>
              {d}
              {ed?.has_override && <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#facc15]" />}
            </button>
          );
        })}
      </div>
      {!hideLegend && (
        <div className="mt-3 pt-3 border-t border-[#1a1a1a] flex items-center gap-3 text-[10px] text-[#7a7a7a]">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#2a1212] border border-[#5b1919]"/>Open</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#dc2626]"/>Selected</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#facc15]"/>Override</span>
        </div>
      )}
    </div>
  );
}

export function pickSmartDefault(dates) {
  if (!dates || dates.length === 0) return null;
  const todayIso = new Date().toISOString().slice(0, 10);
  const sorted = [...dates].sort((a, b) => a.date.localeCompare(b.date));
  // 1. Today
  const today = sorted.find(d => d.date === todayIso);
  if (today) return today;
  // 2. Next upcoming
  const next = sorted.find(d => d.date > todayIso);
  if (next) return next;
  // 3. Most recent past
  return sorted[sorted.length - 1];
}
